import { c as _c } from "react/compiler-runtime";
import type { EChartsOption, SeriesOption, YAXisComponentOption } from 'echarts';
import { useMemo } from 'react';
import type { XYCoordinates } from '@/lib/geometry/coordinates';
import type { KeyWith } from '@/lib/piano/keyboard';
import type { KeyboardPlacement } from '@/lib/piano/keyboard/keyboard-geometry';
import { CHART_HEIGHTS, PLOT_INSET } from '../../chart-geometry';
import { buildKeyAxis, buildValueAxis, scaleFromReadings, scaleOver, MONO_FONT, type AxisScale } from '../../echarts/build-base-option';
import type { ChartTokens } from '../../chart-tokens';
import type { TouchDesignDataPoint, TouchDesignSerie } from '../../interfaces';
import { TOUCH_WEIGHT_SERIE_IDS, type TouchWeightSerieId, type TouchWeightSeries } from './use-touch-weight-series';

/** Room above a panel for the title the design writes there. */
const PLOT_TOP = 20;
/** The weight panel. `chart-geometry` only sizes single-panel plots. */
const WEIGHT_PLOT_HEIGHT = 240;
/** Air between the two panels, enough for the lower one's title. */
const PANE_GAP = 26;

/** The weight panel is the first grid, the friction panel the second. */
const WEIGHT_GRID = 0;
export const FRICTION_GRID = 1;

/**
 * Both panels, their margins and their titles, plus a hair so the lowest rule
 * is not clipped. `extra` is the room a printed sheet has left over, and it
 * all goes to the weight panel: the friction one reads a range of a few grams
 * and gains nothing from being stretched.
 */
export const touchWeightPlotHeight = (extra = 0) => PLOT_TOP + WEIGHT_PLOT_HEIGHT + extra + PANE_GAP + CHART_HEIGHTS.frictionPlot + 2;

/**
 * The scales of the design.
 *
 * They are a floor, not a ceiling: an atypical action is allowed to push a
 * scale further rather than have its readings quietly clipped.
 */
export const WEIGHT_SCALE: AxisScale = {
  interval: 20,
  max: 100,
  min: 0
};
const FRICTION_SCALE: AxisScale = {
  interval: 8,
  max: 24,
  min: 0
};
interface SerieStyle {
  gridIndex: number;
  symbol: string;
  symbolRotate?: number;
  symbolSize: number;
  token: keyof ChartTokens;
  z: number;
}

/** Symbols and sizes read off the mockup, at its drawing scale. */
const SERIE_STYLES: Record<TouchWeightSerieId, SerieStyle> = {
  balance: {
    gridIndex: WEIGHT_GRID,
    symbol: 'diamond',
    symbolSize: 7,
    token: 'seriesBalance',
    z: 4
  },
  down: {
    gridIndex: WEIGHT_GRID,
    symbol: 'triangle',
    symbolRotate: 180,
    symbolSize: 6,
    token: 'seriesDown',
    z: 3
  },
  friction: {
    gridIndex: FRICTION_GRID,
    symbol: 'circle',
    symbolSize: 5,
    token: 'seriesFriction',
    z: 3
  },
  up: {
    gridIndex: WEIGHT_GRID,
    symbol: 'triangle',
    symbolSize: 6,
    token: 'seriesUp',
    z: 3
  }
};
const isMeasured = (point: TouchDesignDataPoint): point is KeyWith<number> & TouchDesignDataPoint => point.payload !== undefined && !Number.isNaN(point.payload);

/** A reading sits above the balance rail slot of its own key. */
const positionOf = (geometry: KeyboardPlacement, keyNumber: number): number => geometry.keysByNumber.get(keyNumber)?.backCenter ?? 0;
const pointsOf = (serie: TouchDesignSerie, geometry: KeyboardPlacement): XYCoordinates[] => serie.data.filter(isMeasured).map(point => [positionOf(geometry, point.number), point.payload]);
const valuesOf = (series: TouchDesignSerie[]): number[] => series.flatMap(serie => serie.data.filter(isMeasured).map(point => point.payload));
const buildPanelAxis = (tokens: ChartTokens, scale: AxisScale, locale: string, gridIndex: number, title: string): YAXisComponentOption => ({
  ...buildValueAxis(tokens, scale, locale),
  gridIndex,
  // The quantity is named above its own panel: with two scales in one drawing,
  // the page heading can no longer say which is which.
  name: title,
  nameGap: 14,
  nameLocation: 'end',
  nameTextStyle: {
    align: 'left',
    color: tokens.ink,
    fontFamily: MONO_FONT,
    fontSize: 10
  }
});
const buildScatterSerie = (serie: TouchDesignSerie, style: SerieStyle, geometry: KeyboardPlacement, tokens: ChartTokens): SeriesOption => ({
  data: pointsOf(serie, geometry),
  itemStyle: {
    color: tokens[style.token]
  },
  name: serie.name,
  silent: true,
  symbol: style.symbol,
  symbolRotate: style.symbolRotate,
  symbolSize: style.symbolSize,
  type: 'scatter',
  xAxisIndex: style.gridIndex,
  yAxisIndex: style.gridIndex,
  z: style.z
});

/** The tie between the down and up readings of one key: the touch's spread. */
const buildRangeSerie = (down: TouchDesignSerie, up: TouchDesignSerie, geometry: KeyboardPlacement, tokens: ChartTokens): SeriesOption => {
  const upByKey = new Map(up.data.filter(isMeasured).map(point => [point.number, point.payload]));
  return {
    coordinateSystem: 'cartesian2d',
    data: down.data.filter(isMeasured).flatMap(point => {
      const upValue = upByKey.get(point.number);
      if (upValue === undefined) {
        return [];
      }
      const position = positionOf(geometry, point.number);
      return [{
        coords: [[position, upValue], [position, point.payload]] as XYCoordinates[]
      }];
    }),
    lineStyle: {
      color: tokens.range,
      width: 1.3
    },
    name: '',
    silent: true,
    type: 'lines',
    xAxisIndex: WEIGHT_GRID,
    yAxisIndex: WEIGHT_GRID,
    z: 1
  };
};

/** The firmer rule at the foot of a panel, which the grid alone does not draw. */
const buildBaselineSerie = (gridIndex: number, tokens: ChartTokens, foot: number): SeriesOption => ({
  data: [],
  markLine: {
    data: [{
      yAxis: foot
    }],
    label: {
      show: false
    },
    lineStyle: {
      color: tokens.axisStrong,
      type: 'solid',
      width: 1.2
    },
    silent: true,
    symbol: 'none'
  },
  name: '',
  silent: true,
  type: 'line',
  xAxisIndex: gridIndex,
  yAxisIndex: gridIndex
});
export interface TouchWeightOptionInput {
  /** Room a printed sheet has left over, which the weight panel takes. */
  extraWeightHeight?: number;
  /** The reference bands behind the friction panel. */
  frictionZones: SeriesOption[];
  geometry: KeyboardPlacement;
  locale: string;
  panelTitles: {
    friction: string;
    weight: string;
  };
  series: TouchWeightSeries;
  tokens: ChartTokens;
  visibleSeries: Readonly<Record<TouchWeightSerieId, boolean>>;
}

/**
 * One drawing, two stacked plots: weight above, friction below.
 *
 * They share the keyboard axis — and the keyboard drawn under the figure — so
 * a key reads as one column across both panels.
 *
 * The key being read is deliberately absent from here: it moves with every
 * pixel of the pointer, and rebuilding this option that often would redraw
 * the whole drawing. The brass band and the lit key are HTML, outside ECharts.
 */
export const useTouchWeightOption = input => {
  const $ = _c(52);
  const {
    extraWeightHeight: t0,
    frictionZones,
    geometry,
    locale,
    panelTitles,
    series,
    tokens,
    visibleSeries
  } = input;
  const extraWeightHeight = t0 === undefined ? 0 : t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== extraWeightHeight || $[1] !== frictionZones || $[2] !== geometry || $[3] !== locale || $[4] !== panelTitles.friction || $[5] !== panelTitles.weight || $[6] !== series || $[7] !== tokens || $[8] !== visibleSeries) {
    const weightScale = scaleFromReadings(WEIGHT_SCALE, valuesOf([series.down, series.up, series.balance]));
    const frictionScale = scaleOver(FRICTION_SCALE, valuesOf([series.friction]));
    let t9;
    if ($[17] !== visibleSeries) {
      t9 = id => visibleSeries[id];
      $[17] = visibleSeries;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== geometry || $[20] !== series || $[21] !== tokens) {
      t10 = id_0 => buildScatterSerie(series[id_0], SERIE_STYLES[id_0], geometry, tokens);
      $[19] = geometry;
      $[20] = series;
      $[21] = tokens;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    const echartsSeries = [...frictionZones, buildBaselineSerie(WEIGHT_GRID, tokens, weightScale.min), buildBaselineSerie(FRICTION_GRID, tokens, frictionScale.min), ...(visibleSeries.down && visibleSeries.up ? [buildRangeSerie(series.down, series.up, geometry, tokens)] : []), ...TOUCH_WEIGHT_SERIE_IDS.filter(t9).map(t10)];
    t3 = false;
    const t11 = WEIGHT_PLOT_HEIGHT + extraWeightHeight;
    let t12;
    if ($[23] !== t11) {
      t12 = {
        height: t11,
        left: PLOT_INSET.left,
        right: PLOT_INSET.right,
        top: PLOT_TOP
      };
      $[23] = t11;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    const t13 = PLOT_TOP + WEIGHT_PLOT_HEIGHT + extraWeightHeight + PANE_GAP;
    let t14;
    if ($[25] !== t13) {
      t14 = {
        height: CHART_HEIGHTS.frictionPlot,
        left: PLOT_INSET.left,
        right: PLOT_INSET.right,
        top: t13
      };
      $[25] = t13;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    if ($[27] !== t12 || $[28] !== t14) {
      t4 = [t12, t14];
      $[27] = t12;
      $[28] = t14;
      $[29] = t4;
    } else {
      t4 = $[29];
    }
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        show: false
      };
      $[30] = t5;
    } else {
      t5 = $[30];
    }
    t6 = echartsSeries;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        show: false
      };
      $[31] = t7;
    } else {
      t7 = $[31];
    }
    let t15;
    if ($[32] !== geometry) {
      t15 = buildKeyAxis(geometry);
      $[32] = geometry;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    let t16;
    if ($[34] !== geometry) {
      t16 = buildKeyAxis(geometry);
      $[34] = geometry;
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    let t17;
    if ($[36] !== t16) {
      t17 = {
        ...t16,
        gridIndex: FRICTION_GRID
      };
      $[36] = t16;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    if ($[38] !== t15 || $[39] !== t17) {
      t8 = [t15, t17];
      $[38] = t15;
      $[39] = t17;
      $[40] = t8;
    } else {
      t8 = $[40];
    }
    t1 = buildPanelAxis(tokens, weightScale, locale, WEIGHT_GRID, panelTitles.weight);
    t2 = buildPanelAxis(tokens, frictionScale, locale, FRICTION_GRID, panelTitles.friction);
    $[0] = extraWeightHeight;
    $[1] = frictionZones;
    $[2] = geometry;
    $[3] = locale;
    $[4] = panelTitles.friction;
    $[5] = panelTitles.weight;
    $[6] = series;
    $[7] = tokens;
    $[8] = visibleSeries;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[41] !== t1 || $[42] !== t2) {
    t9 = [t1, t2];
    $[41] = t1;
    $[42] = t2;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  let t10;
  if ($[44] !== t3 || $[45] !== t4 || $[46] !== t5 || $[47] !== t6 || $[48] !== t7 || $[49] !== t8 || $[50] !== t9) {
    t10 = {
      animation: t3,
      grid: t4,
      legend: t5,
      series: t6,
      tooltip: t7,
      xAxis: t8,
      yAxis: t9
    };
    $[44] = t3;
    $[45] = t4;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
    $[49] = t8;
    $[50] = t9;
    $[51] = t10;
  } else {
    t10 = $[51];
  }
  return t10;
};